import type {
  FlexProps
} from '@chakra-ui/react'
import {
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Switch,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { userListContext } from 'BrokerTsLib/src/context'
import { useContext, useMemo } from 'react'

import { SearchField } from '@/components/SearchField'
import { StyledMenuItem } from '@/layouts/components/Header'
import { faArrowUpFromArc } from '@fortawesome/pro-light-svg-icons'
import { faCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { Table } from '@tanstack/react-table'
import { SplitButton } from 'BrokerTsLib/src/components'
import type { UserGridItem } from 'BrokerTsLib/src/dtos'
import { useToggleIsActiveUser } from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { UserTransferModal } from './UserTransferModal'

export function UserListFilter<TGridItem extends UserGridItem>({ table, ...rest }: FlexProps & { table: Table<TGridItem> }) {
  const { filter, updateFilter, reload } = useContext(userListContext)
  const { isOpen: isOpenTransformModal, onClose: onCloseTranformModal, onOpen: onOpenTranformModal } = useDisclosure()
  const { mutateAsync: toggleIsActiveAsync } = useToggleIsActiveUser()
  const toast = useToast()

  const selectedUsers = table
    .getSelectedRowModel()
    .rows
    .map(row => row.original)

  const selectedUserIds = selectedUsers.map(user => user.userId)

  const refreshGrid = () => {
    const result = reload() as Promise<any>

    result.then(() => {
      table.resetRowSelection()
    })
  }

  const arr = useMemo(() => {
    const actions = [
      <StyledMenuItem
        key={1}
        variant=''
        onClick={onOpenTranformModal}
        to={''}
      >
        <Icon as={FontAwesomeIcon} icon={faArrowUpFromArc} size="sm" color="secondary.500" mr={2} />
        Přesun
      </StyledMenuItem>
    ]

    if (selectedUsers.find(user => user.valid)) {
      actions.push(
        <StyledMenuItem
          key={1}
          variant=''
          onClick={() => {
            toggleIsActiveAsync({ isActive: false, userIds: selectedUserIds })
              .then(() => {
                refreshGrid()
                toast({ title: selectedUserIds.length > 1 ? 'Uživatelé byli deaktivování.' : 'Uživatel byl deaktivován.', status: 'success' })
              })
              .catch(() => {
                toast({ title: 'Při deaktivaci nastala neočekávaná chyba.', status: 'error' })
              })
          }}
          to={''}
        >
          <Icon as={FontAwesomeIcon} icon={faCircle} size="sm" color="red.500" mr={2} />
          Deaktivovat
        </StyledMenuItem>
      )
    }

    if (selectedUsers.find(user => !user.valid)) {
      actions.push(
        <StyledMenuItem
          key={1}
          variant=''
          onClick={() => {
            toggleIsActiveAsync({ isActive: true, userIds: selectedUserIds })
              .then(() => {
                refreshGrid()
                toast({ title: selectedUserIds.length > 1 ? 'Uživatelé byli aktivování.' : 'Uživatel byl aktivován.', status: 'success' })
              })
              .catch(() => {
                toast({ title: 'Při aktivaci nastala neočekávaná chyba.', status: 'error' })
              })
          }}
          to={''}
        >
          <Icon as={FontAwesomeIcon} icon={faCircle} size="sm" color="green.500" mr={2} />
          Aktivovat
        </StyledMenuItem>
      )
    }

    return actions
  }, [selectedUsers])

  return (
    <Flex
      justifyContent={'space-between'}
      {...rest}
      flexDir={{ base: 'column', sm: 'row' }}
      gap={4}
    >
      <Flex gap={8}>
        <SearchField
          onSearch={(search) => {
            table.resetRowSelection()
            updateFilter({ ...filter, name: search, pageIndex: 1 })
          }
          }
        />

        {selectedUsers.length > 0 &&
          <SplitButton
            variant=''
            stackProps={{ sx: { bg: 'white' } }}
            buttonProps={{
              size: 'sm',
              disabled: true,
              pointerEvents: 'none',
              as: StyledMenuItem,
              children: `Hromadné akce (${selectedUsers.length})`,
            }}
            splitterColor="primary.600"
            menuItems={arr.length == 0 ? undefined : arr}
            popoverStyle={{}} />
        }
      </Flex>

      <FormControl display="flex" alignItems="center">
        <Switch
          size="md"
          id="showInactive"
          isChecked={filter.showInvalid}
          onChange={() => {
            updateFilter({
              ...filter,
              showInvalid: !filter.showInvalid,
              pageIndex: 1,
            })
          }}
        />
        <FormLabel htmlFor="showInactive" variant="switch">
          Zobrazit i neaktivní
        </FormLabel>
      </FormControl>

      <UserTransferModal
        userIds={selectedUsers.map(user => user.userId)}
        organizationUnitId={selectedUsers.length == 1 ? selectedUsers[0].organizationUnitId : undefined}
        isOpen={isOpenTransformModal}
        onClose={onCloseTranformModal}
        transferSuccessCallback={refreshGrid}
      />
    </Flex>
  )
}
